import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';

/**
 * CRUD hook for the task/to-do board (personal, entertainment and branch scoped
 * tasks, including feedback-generated accountability tasks).
 */
const useTodo = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  const getBoard = useCallback(
    async ({ status = null, entertainmentId = null, branchId = null, includeCompleted = false } = {}) => {
      try {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (entertainmentId) params.append('entertainmentId', entertainmentId);
        if (branchId) params.append('branchId', branchId);
        if (includeCompleted) params.append('includeCompleted', 'true');

        return await sendRequest({
          url: `/api/v1/todo?${params.toString()}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden der Aufgaben');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getCount = useCallback(async () => {
    try {
      return await sendRequest({
        url: `/api/v1/todo/count`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    } catch (err) {
      toast.error('Fehler beim Laden der Aufgabenanzahl');
      throw err;
    }
  }, [authCtx, sendRequest]);

  const getTags = useCallback(async () => {
    try {
      return await sendRequest({
        url: `/api/v1/todo/tags`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    } catch (err) {
      toast.error('Fehler beim Laden der Tags');
      throw err;
    }
  }, [authCtx, sendRequest]);

  const createTodo = useCallback(
    async (payload) => {
      try {
        return await sendRequest({
          url: `/api/v1/todo`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: payload,
        });
      } catch (err) {
        toast.error('Fehler beim Erstellen der Aufgabe');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const updateTodo = useCallback(
    async (id, payload) => {
      try {
        return await sendRequest({
          url: `/api/v1/todo/${id}`,
          method: 'PATCH',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: payload,
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren der Aufgabe');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const completeTodo = useCallback(
    async (id, note = null) => {
      try {
        return await sendRequest({
          url: `/api/v1/todo/${id}/complete`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { note },
        });
      } catch (err) {
        toast.error('Fehler beim Erledigen der Aufgabe');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const approveTodo = useCallback(
    async (id) => {
      try {
        return await sendRequest({
          url: `/api/v1/todo/${id}/approve`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
        });
      } catch (err) {
        toast.error('Fehler beim Freigeben der Aufgabe');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const rejectTodo = useCallback(
    async (id, note = null) => {
      try {
        return await sendRequest({
          url: `/api/v1/todo/${id}/reject`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { note },
        });
      } catch (err) {
        toast.error('Fehler beim Ablehnen der Aufgabe');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Subtasks ("Unterpunkte") — checklist items below a developer to-do.
  const addSubtask = useCallback(
    async (todoId, title) => {
      try {
        return await sendRequest({
          url: `/api/v1/todo/${todoId}/subtasks`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { title },
        });
      } catch (err) {
        toast.error('Fehler beim Hinzufügen des Unterpunkts');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const updateSubtask = useCallback(
    async (todoId, subtaskId, payload) => {
      try {
        return await sendRequest({
          url: `/api/v1/todo/${todoId}/subtasks/${subtaskId}`,
          method: 'PATCH',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: payload,
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Unterpunkts');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const deleteSubtask = useCallback(
    async (todoId, subtaskId) => {
      try {
        return await sendRequest({
          url: `/api/v1/todo/${todoId}/subtasks/${subtaskId}`,
          method: 'DELETE',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Löschen des Unterpunkts');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // "Dev Issues" kanban board: every developer todo as exactly one card. The
  // board only reads the tasks - cards are never created here.
  const getDevBoard = useCallback(async () => {
    try {
      return await sendRequest({
        url: `/api/v1/todo/dev-board`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    } catch (err) {
      toast.error('Fehler beim Laden der Dev Issues');
      throw err;
    }
  }, [authCtx, sendRequest]);

  // Moves a card into another column. 'done' completes the task, moving out of
  // 'done' reopens it; the backend informs the author of the source feedback.
  const moveDevBoardCard = useCallback(
    async (id, status) => {
      try {
        return await sendRequest({
          url: `/api/v1/todo/${id}/dev-board`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { status },
        });
      } catch (err) {
        toast.error('Fehler beim Verschieben der Karte');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Marks a developer card for automatic processing, or takes it back out. The
  // marking is the approval the scheduled run stands on: it later ticks the
  // task off on its own, which writes to the author of the source feedback.
  const setAutoRun = useCallback(
    async (id, enabled) => {
      try {
        return await sendRequest({
          url: `/api/v1/todo/${id}/auto-run`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { enabled },
        });
      } catch (err) {
        toast.error('Fehler beim Ändern der automatischen Abarbeitung');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const deleteTodo = useCallback(
    async (id) => {
      try {
        return await sendRequest({
          url: `/api/v1/todo/${id}`,
          method: 'DELETE',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Löschen der Aufgabe');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  return {
    getBoard,
    getCount,
    getTags,
    createTodo,
    updateTodo,
    completeTodo,
    approveTodo,
    rejectTodo,
    deleteTodo,
    getDevBoard,
    moveDevBoardCard,
    setAutoRun,
    addSubtask,
    updateSubtask,
    deleteSubtask,
  };
};

export default useTodo;
